import { Component, OnInit } from '@angular/core';
import { tr, DialogService, DialogResult, KpdNavigator } from '@kpd/framework-ngx';
import { PermissionsetsNavigatorHelper } from 'app/permissionsets/permissionsets-navigator-helper';
import { PermissionsetFacade } from 'app/shared/services/facades/permissionset.facade';
import { PermissionsetKey } from 'app/shared/models/keys/permissionset-key';
import { of } from 'rxjs';
import { switchMap, catchError, tap } from 'rxjs/operators';
import { Permissions } from 'app/shared/services/permissions';
import { Permissionset } from 'app/shared/models/permissionset';
import { PermissionsetService } from 'app/shared/services/permissionset.service';

@Component({
  selector: 'app-permission-sets-sidepanel',
  templateUrl: './permissionsets-sidepanel.component.html',
})
export class PermissionsetsSidepanelComponent implements OnInit {
  permissionSet: Permissionset | null;
  Permissions: typeof Permissions = Permissions;
  navigatorHelper: PermissionsetsNavigatorHelper;
  userCount = 0;

  constructor(
    public permissionSetFacade: PermissionsetFacade,
    private permissionSetService: PermissionsetService,
    private dialogService: DialogService,
    private navigator: KpdNavigator,
  ) {
    this.navigatorHelper = new PermissionsetsNavigatorHelper(navigator);
  }

  ngOnInit(): void {}

  import(): void {
    this.dialogService
      .open('wizard')
      .afterClosed()
      .subscribe((result) => {
        if (result === DialogResult.Verify) {
          this.permissionSetFacade.loadData();
        }
      });
  }

  onAdd(): void {
    this.permissionSetFacade.clearSelection();
    this.navigatorHelper.navigateToCreateFromDetail(true);
  }

  onView(permissionSetKey: PermissionsetKey): void {
    this.navigatorHelper.navigateToConsult(permissionSetKey);
  }

  onEdit(permissionSetKey: PermissionsetKey): void {
    this.navigatorHelper.navigateToEdit(permissionSetKey);
  }

  onCopy(permissionSetId: PermissionsetKey): void {
    this.permissionSetService
      .getById(permissionSetId)
      .pipe(
        tap((result) => (this.permissionSet = result)),
        switchMap(() => {
          return this.dialogService.open('copyPermissionSet', this.permissionSet).afterClosed();
        }),
      )
      .subscribe((result) => {
        this.permissionSet = null;
        if (result) {
          this.navigatorHelper.navigateToConsult(result.key, true);
        }
      });
  }

  onDelete(selectedIds: PermissionsetKey[]): void {
    if (selectedIds.some((id) => (id as PermissionsetKey).isGlobalPermission)) {
      this.dialogService
        .open('alert', {
          message: tr('Global permissionsets can not be deleted'),
          header: tr('Delete failed'),
          type: 'error',
        })
        .afterClosed();
      return;
    }

    const message = tr('Are you sure to delete one (or more) selected permission set(s)?');
    this.dialogService
      .open('confirm', {
        message,
        header: tr('Delete permission set(s)'),
        verifyButtonText: tr('Delete'),
        verifyButtonStyle: 'danger',
        cancelButtonText: tr('Cancel'),
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (result === DialogResult.Verify) {
            return this.permissionSetFacade.deleteItems(selectedIds);
          }
          return of({});
        }),
        catchError((error: any) => {
          if (error.status === 409 || error.status === 400) {
            return this.dialogService
              .open('confirm', {
                message: tr(`Cannot delete permission set due to conflict(s)`) + ` : ${error.details.error[0]}`,
                header: tr('Delete failed'),
                hideVerify: true,
                cancelButtonText: tr('Ok'),
              })
              .afterClosed();
          }
          throw error;
        }),
      )
      .subscribe();
  }

  navigateToPermissions(key: PermissionsetKey): void {
    if (key.isGlobalPermission) {
      this.navigator.navigateChild(
        ['permissionsets', 'details', 'application', key.applicationId, 'permissionset', 'global', key.permissionSetId],
        {
          queryParams: { tab: 'permissions' },
        },
      );
    } else {
      this.navigator.navigateChild(
        ['permissionsets', 'details', 'application', key.applicationId, 'permissionset', key.permissionSetId],
        {
          queryParams: { tab: 'permissions' },
        },
      );
    }
  }
}
